<script setup lang="ts">
import { convertLexicalToHTML } from '@payloadcms/richtext-lexical/html'
import { parse } from '@node-projects/node-html-parser-esm'
import { flatMap } from 'lodash-es'
import { defaultRichTextSizeClass, isValidRichTextSizeClass, normalizeRichTextSizeClass } from '~/unocss/text-sizes'

const props = defineProps({
  content: {
    required: true,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  textSizeClass: {
    type: String,
    default: defaultRichTextSizeClass,
    validator: isValidRichTextSizeClass,
  },
})

const sizeClass = computed(() => normalizeRichTextSizeClass(props.textSizeClass))

const cleanHtml = computed(() => {
  const html = convertLexicalToHTML({
    data: props.content,
    disableContainer: true,
    disableTextAlign: true,
    converters: (({ defaultConverters }) => ({
      ...defaultConverters,
      // delegate children to the default converters so linebreaks, formatting
      // and nested nodes inside a link survive
      link: ({ node, nodesToHTML }: any) => {
        const innerHtml = nodesToHTML({ nodes: node.children ?? [] }).join('')

        return `<a href="/" id="${node.id}" target="_blank" rel="noopener noreferrer">${innerHtml}</a>`
      }
    }))
  })

  return html
    ?.replace(/<p><br\s*\/?><\/p>/g, '')
    ?.replace(/\u00a0/g, ' ')
    // Underline is no longer an editor option; strip it from existing content
    ?.replace(/<\/?u>/g, '')
    ?.replace(/text-decoration:\s*underline;?/g, '')
})

const nodes = computed(() => parse(cleanHtml.value))

const linkNodesFromContent = computed(() => {
  const getLinkNodesRecursively = (node: Node) => {
    if (node?.type === 'link') {
      return [node]
    }

    if (node?.children) {
      return flatMap(node.children, getLinkNodesRecursively)
    }

    return []
  }

  return getLinkNodesRecursively(props?.content?.root)
})
</script>

<template>
  <component
    :is="inline ? 'span' : 'div'"
    class="prose max-w-none"
    :class="sizeClass"
  >
    <core-rich-text-node
      v-for="(node, index) in nodes.childNodes"
      :key="index"
      :node="node"
      :link-objects="linkNodesFromContent"
      :is-first="index === 0"
      :is-last="index === nodes.childNodes.length - 1"
    />
  </component>
</template>
